import PropTypes from 'prop-types';

import {useSubscriber} from '@stubhub/pubsub-utils';
import {connect} from '@stubhub/react-store-provider';

import controller from './controller';
import {GUARANTEE_CC} from './guarantee-cc';
import {PAYOUT_METHODS_MAP} from './payout-methods';
import {SELL_FLOW_EVENTS, SELL_HUB_EVENTS, SELL_CHATBOT_EVENTS} from './track-enum';
import {screenView, getUserGUID, beginPublish, sellDenied, error, successPublish, addPrice, stringToFixedFloat, sellAddAccountInfo, addSellerType, addFulfillment, addTicketDetails, chatbot} from './track-utils';

const TrackListener = (props, {track, cookies}) => {
  const {sellFlowStart, eventId, eventName, daysToEvent, genre, inventoryMarket, ancestors, countryCode, errorName, eventDateUTC, onSaleDate, imageURL, faceValue, ticketCurrency, ticketPrice, medium, fulFillmentMethod, deliveryNow, numTickets} = props;
  const sharedScreenView = (screenName) => {
    const {storeId, locale, storeCountry, isAdvisoryCurrencyEnabled, advisoryCurrency} = props;
    const userGUID = getUserGUID(cookies);
    screenView(track, {screenName, storeId, storeCountry, locale, isAdvisoryCurrencyEnabled, advisoryCurrency, userGUID});
  };

  const sellHubTracking = {
    [SELL_HUB_EVENTS.LOADED]: () => sharedScreenView('sell_hub'),
    [SELL_HUB_EVENTS.SEARCH_RESULTS]: () => sharedScreenView('sell_search'),
    [SELL_HUB_EVENTS.ERROR]: () => {
      sharedScreenView('sell_denied');
      sellDenied(track, {sellFlowStart, eventId, eventName, daysToEvent, genre, inventoryMarket, ancestors, countryCode, onSaleDate, imageURL, eventDateUTC});
      error(track, {error: errorName, name: 'sell_denied'});
    },
    [SELL_FLOW_EVENTS.ADD_PRICE]: () => {
      addPrice(track, {faceValue: stringToFixedFloat(faceValue), price: stringToFixedFloat(ticketPrice), currency: ticketCurrency});
    },
    [SELL_FLOW_EVENTS.ADD_SELLER_TYPE]: ({sellerType}) => {
      addSellerType(track, {sellerType});
    },
    [SELL_FLOW_EVENTS.ADD_FULFILLMENT]: () => {
      const ticketsInHand = deliveryNow ? 'ready' : 'later';
      addFulfillment(track, {ticketMedium: medium?.toLowerCase(), fulfillment: fulFillmentMethod?.toLowerCase(), ticketsInHand, ticketQuantity: numTickets});
    }
  };

  const sellFlowTracking = {
    [SELL_FLOW_EVENTS.LISTING_DETAILS_LOADED]: () => {
      const {eventId, eventName, countryCode, daysToEvent, genre, inventoryMarket, ancestors, fulfillmentMethods, sellFlowStart} = props;
      sharedScreenView('sell_listing_details');
      beginPublish(track, {
        eventId,
        eventName,
        countryCode,
        daysToEvent,
        genre,
        inventoryMarket,
        ancestors,
        fulfillmentMethods,
        sellFlowStart,
        onSaleDate,
        imageURL,
        eventDateUTC
      });
    },
    [SELL_FLOW_EVENTS.LISTING_CREATED]: () => {
      const {eventId, eventName, countryCode, daysToEvent, genre, inventoryMarket, ancestors, fulfillmentMethods, medium, fulFillmentMethod, deliveryNow, numTickets, faceValue, ticketPrice, ticketCurrency, numFeatures, connections, numRestrictions, sellerType, paymenMethod, sellFlowStart, listingId} = props;
      sharedScreenView('sell_publish_success');
      successPublish(track, {
        eventId,
        eventName,
        countryCode,
        daysToEvent,
        genre,
        inventoryMarket,
        ancestors,
        fulfillmentMethods,
        medium,
        fulFillmentMethod,
        deliveryNow,
        numTickets,
        faceValue,
        ticketPrice,
        ticketCurrency,
        numFeatures,
        connections,
        numRestrictions,
        sellerType,
        paymenMethod,
        sellFlowStart,
        listingId,
        onSaleDate,
        imageURL,
        eventDateUTC
      });
    },
    [SELL_FLOW_EVENTS.ACCOUNT_DETAILS]: ({payoutMethod, guaranteeCC}) => {
      sellAddAccountInfo(track, {
        payout_method: PAYOUT_METHODS_MAP[payoutMethod] ? PAYOUT_METHODS_MAP[payoutMethod] : null,
        guarantee_cc: guaranteeCC === undefined ? GUARANTEE_CC.NEW : GUARANTEE_CC.EXISTING
      });
    },
    [SELL_FLOW_EVENTS.ADD_TICKET_DETAILS]: () => {
      const {featuresQuantity, restrictionsQuantity, connected, limitationsQuantity} = props;
      addTicketDetails(track, {
        featuresQuantity,
        restrictionsQuantity,
        connected,
        limitationsQuantity
      });
    }
  };

  const chatbotTracking = {
    [SELL_CHATBOT_EVENTS.OPEN]: () => {
      const {storeId, locale, storeCountry} = props;
      chatbot(track, {
        storeId,
        storeCountry,
        locale,
        status: 'open'
      });
    },
    [SELL_CHATBOT_EVENTS.CLOSE]: () => {
      const {storeId, locale, storeCountry} = props;
      chatbot(track, {
        storeId,
        storeCountry,
        locale,
        status: 'close'
      });
    }
  };

  useSubscriber(SELL_HUB_EVENTS.MAIN, sellHubTracking);
  useSubscriber(SELL_FLOW_EVENTS.MAIN, sellFlowTracking);
  useSubscriber(SELL_CHATBOT_EVENTS.MAIN, chatbotTracking);

  return null;
};

TrackListener.contextTypes = {
  track: PropTypes.object,
  cookies: PropTypes.object
};

TrackListener.propTypes = {
  sellFlowStart: PropTypes.string,
  eventId: PropTypes.string,
  eventName: PropTypes.string,
  daysToEvent: PropTypes.number,
  genre: PropTypes.string,
  inventoryMarket: PropTypes.string,
  ancestors: PropTypes.array,
  countryCode: PropTypes.string,
  errorName: PropTypes.string,
  eventDateUTC: PropTypes.string,
  onSaleDate: PropTypes.string,
  imageURL: PropTypes.string,
  faceValue: PropTypes.string,
  ticketCurrency: PropTypes.string,
  ticketPrice: PropTypes.string,
  medium: PropTypes.string,
  fulFillmentMethod: PropTypes.string,
  deliveryNow: PropTypes.bool,
  numTickets: PropTypes.number,
  storeId: PropTypes.string,
  locale: PropTypes.string,
  storeCountry: PropTypes.string,
  isAdvisoryCurrencyEnabled: PropTypes.bool,
  advisoryCurrency: PropTypes.string,
  fulfillmentMethods: PropTypes.array,
  sellerType: PropTypes.string,
  paymenMethod: PropTypes.string,
  listingId: PropTypes.string,
  featuresQuantity: PropTypes.number,
  restrictionsQuantity: PropTypes.number,
  connected: PropTypes.bool,
  limitationsQuantity: PropTypes.number
};

export default connect(controller)(TrackListener);
